import * as React from 'react'
import { styled, css } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import { useCopy } from '@owl-nest/copier'
import { t } from '@owl-nest/localize'
import * as shadow from '@owl-nest/shadow'
import { getSharingURL, Platform, useSharing } from '@owl-nest/sharing'
import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../models/project'
import { TRACKING_SOURCE } from '../../utils/trackerEnums'

type SharePropTypes = {
  project: projectModel.Project
  user: api.PublicUser
}

export function Share({ project, user }: SharePropTypes): React.ReactElement<SharePropTypes> {
  const { canCopy, copy, copyStatus } = useCopy(3000)
  const tracking = shadow.useTracking()
  const { shareTo } = useSharing<projectModel.ExtraFields>('project-page', project, user)

  return (
    <SharesContainer>
      <InformationContainer>
        <plume.styles.heading.XXS asDesktop="S">
          <plume.illustrations.twoToned.Share size={52} inverted noBackground />
          {t('Share to boost this project')}
        </plume.styles.heading.XXS>

        <Buttons>
          <Link onClick={share('facebook')}>
            <plume.glyphs.thirdParty.Facebook size={16} />
          </Link>
          <Link onClick={share('messenger')}>
            <plume.glyphs.thirdParty.Messenger size={16} />
          </Link>
          <Link onClick={share('twitter')}>
            <plume.glyphs.thirdParty.Twitter size={16} />
          </Link>
          <Link onClick={share('linkedin')}>
            <plume.glyphs.thirdParty.Linkedin size={16} />
          </Link>
          <Link onClick={share('whatsapp')}>
            <plume.glyphs.thirdParty.WhatsApp size={16} />
          </Link>
          {canCopy && copyStatus && (
            <plume.styles.tooltip.BlockAnchor>
              <plume.Tooltip fitToContent open={copyStatus !== 'idle'}>
                {copyStatus === 'succeeded' ? (
                  <>
                    <plume.glyphs.stroke.CheckCircle size={16} />
                    <plume.styles.copy.S.regular>{t('Link copied to your clipboard.')}</plume.styles.copy.S.regular>
                  </>
                ) : (
                  <>
                    <plume.glyphs.stroke.TimesCircle size={16} />
                    <plume.styles.copy.S.regular>
                      {t("Sorry, we couldn't copy the link. Please try again!")}
                    </plume.styles.copy.S.regular>
                  </>
                )}
              </plume.Tooltip>

              <Link
                onClick={() => {
                  const projectURL = getSharingURL<projectModel.ExtraFields>('project-page', 'clipboard', project, user)
                  tracking.social.track({
                    eventCategory: shadow.CATEGORY.PROJECT_PAGE,
                    eventLabel: TRACKING_SOURCE.BACKERS,
                    socialAction: 'click: Share',
                    socialNetwork: shadow.SOCIAL_NETWORK.HYPERLINK,
                    socialTarget: project.absolute_url,
                  })
                  copy(projectURL)
                }}
              >
                <plume.glyphs.thirdParty.WebsiteLink size={16} />
              </Link>
            </plume.styles.tooltip.BlockAnchor>
          )}
        </Buttons>
      </InformationContainer>
    </SharesContainer>
  )

  function share(plateform: Platform): () => void {
    return () => {
      shareTo(plateform, { eventCategory: shadow.CATEGORY.PROJECT_PAGE, eventLabel: TRACKING_SOURCE.BACKER_BLOCK })
    }
  }
}

const SharesContainer = styled.div`
  display: block;
  margin-bottom: 14px;
  padding: 20px 20px 10px;
  text-align: center;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
    margin-bottom: 0px;
    padding: 0px;
    text-align: left;
    width: 100%;
  }
`

const iconMargin = css`
  margin-right: 6px;
  margin-top: 2px;
`

const InformationContainer = styled.div`
  box-sizing: border-box;
  padding: 0;
  width: 100%;

  ${plume.styles.heading.XXS} {
    display: block;
    margin-bottom: 15px;
  }

  ${plume.styles.tooltip.BlockAnchor} {
    display: inline-block;

    ${plume.glyphs.stroke.CheckCircle} {
      color: ${plume.COLORS.SECONDARY_GREEN_500};
      ${iconMargin};
    }

    ${plume.glyphs.stroke.TimesCircle} {
      color: ${plume.COLORS.PRIMARY_RED_500};
      ${iconMargin};
    }

    ${plume.Tooltip} {
      margin-bottom: 16px;
    }
  }

  // NOT SCREEN !
  @media not screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.heading.XXS} {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;

      ${plume.illustrations.Illustration} {
        margin-bottom: 17px;
      }
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 0px;

    ${plume.styles.heading.XXS} {
      text-align: center;

      ${plume.illustrations.Illustration} {
        margin-right: 17px;
      }
    }
  }
`

const Link = styled.button`
  align-content: center;
  align-items: center;
  background-color: ${plume.COLORS.SECONDARY_GREEN_800};
  border: 0;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  margin: 0 6px;
  padding: 0;
  width: 32px;

  ${plume.glyphs.Icon} {
    fill: ${plume.COLORS.PRIMARY_GREY_000};
  }
`

const Buttons = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;

  ${plume.styles.tooltip.BlockAnchor} {
    width: 32px;
    height: 32px;
  }
`
